import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { FILTER_ENUMS } from 'search/enums/constants';
import { SearchBoxButton } from 'search/styles/SearchStyles';
import { getUpcomingReleasesDateRange } from 'search/utils/getUpcomingReleasesDateRange';
import {
  getYearRangeFromBucket,
  shouldShowPlusSign,
  transformArrayToYearBucketValue,
  transformBucketValueToRange,
} from 'search/utils/mobileYearHelpers';
import { removeDoubleQuotes } from 'search/utils/removeDoubleQuotes';
import { t } from 'localization/utils/t';
import { getDateRangeStrings } from 'search/enums/translations';

const TP = 'search.components.searchGrid.SearchFilterYearMobile';

const Slider = dynamic(() => import('shared/components/Slider').then((mod) => mod.Slider), {
  ssr: false,
});

export const SearchFilterYearMobile = ({
  facet,
  selectedFilterValues,
  setSelectedFilterValues,
}) => {
  const yearBucketSelectedFilterValues = selectedFilterValues?.[FILTER_ENUMS.YEAR_BUCKET];
  const upcomingReleasesSelectedFilterValues = selectedFilterValues?.[FILTER_ENUMS.RELEASE_DATE];
  const upcomingReleasesSort =
    selectedFilterValues?.sortBy === 'release_date' &&
    selectedFilterValues?.sortOrder === 'ascending';
  // values coming back from Constructor are wrapped in double quotes (eg. '"1990"-"2000"')
  const options = facet?.options?.map((option) => ({
    ...option,
    value: removeDoubleQuotes(option.value),
  }));
  const formattedSelectedFilterValues = (
    Array.isArray(yearBucketSelectedFilterValues)
      ? yearBucketSelectedFilterValues
      : [yearBucketSelectedFilterValues]
  )?.map((val) => removeDoubleQuotes(val));
  const isUpcomingReleasesSelected =
    upcomingReleasesSelectedFilterValues &&
    Object.values(upcomingReleasesSelectedFilterValues).length > 0 &&
    upcomingReleasesSort;

  const range: [number, number] = getYearRangeFromBucket(options);
  const initialState = transformBucketValueToRange(
    formattedSelectedFilterValues,
    isUpcomingReleasesSelected,
  );
  const [values, setValues] = useState(initialState || range);
  const [update, setUpdate] = useState(initialState || range);
  const areMultipleRangesSelected = formattedSelectedFilterValues.length > 1;

  const handleSlideEnd = (updatedValues: [number, number]) => {
    setValues(updatedValues);
    const bucketValue = transformArrayToYearBucketValue(updatedValues);
    setSelectedFilterValues({
      [FILTER_ENUMS.YEAR_BUCKET]: bucketValue,
    });
  };

  const handleSlideUpdate = (updatedValues) => {
    setUpdate(updatedValues);
  };

  const forceSliderUpdate = (updatedValues) => {
    setValues(updatedValues);
    setUpdate(updatedValues);
  };

  const handleOnChange = (optionValue) => {
    // We filter out options that do not have a count greater than 0, because that means
    // it is not a default bucket provided by Constructor. It is actually a price range we
    // have set, and we need to remove it when we select a quick filter bucket
    const filteredOutRangeSelection = formattedSelectedFilterValues.filter((val) => {
      const currentOption = options.find((option) => option.value === val);
      return currentOption?.count > 0;
    });

    const isOptionAlreadySelected = filteredOutRangeSelection?.includes(optionValue);
    if (isOptionAlreadySelected) {
      const updatedValues = filteredOutRangeSelection.filter((value) => {
        return value !== optionValue;
      });
      setSelectedFilterValues({
        [FILTER_ENUMS.YEAR_BUCKET]: updatedValues,
      });
      // if only 1 bucket is selected, move the sliders to that bucket range
      const isOnlyOneYearBucketSelected = updatedValues?.length === 1;
      if (isOnlyOneYearBucketSelected) {
        forceSliderUpdate(transformBucketValueToRange(updatedValues, isUpcomingReleasesSelected));
      }
      // when no buckets are selected, reset sliders back to initial range
      if (updatedValues?.length === 0) {
        forceSliderUpdate(range);
      }
    } else {
      const updatedValues = [...filteredOutRangeSelection, optionValue];
      setSelectedFilterValues({
        [FILTER_ENUMS.YEAR_BUCKET]: updatedValues,
      });
      // if only 1 bucket is selected, move the sliders to that bucket range
      const isOnlyOneYearBucketSelected = updatedValues?.length === 1;
      if (isOnlyOneYearBucketSelected) {
        forceSliderUpdate(transformBucketValueToRange(updatedValues, isUpcomingReleasesSelected));
      }
    }
  };

  const handleUpcomingReleasesClick = () => {
    // Upcoming Releases is already selected, so we unselect it
    if (isUpcomingReleasesSelected) {
      setSelectedFilterValues({});
      // reset sliders back to initial range
      forceSliderUpdate(range);
    } else {
      // Select Upcoming Releases
      const { min, max } = getUpcomingReleasesDateRange();
      const releaseDateValue = `${min}-${max}`;
      setSelectedFilterValues({
        [FILTER_ENUMS.RELEASE_DATE]: releaseDateValue,
        sortBy: 'release_date', // BUY3-2027: we want to sort by release date, ascending
        sortOrder: 'ascending',
      });
      forceSliderUpdate(transformBucketValueToRange(['2022-inf'], isUpcomingReleasesSelected));
    }
  };

  return (
    <>
      {areMultipleRangesSelected ? (
        <SearchFilterYearMobile.MultipleRangesSelected>
          {t(`${TP}.multipleRangesSelected`, 'Multiple Ranges Selected')}
        </SearchFilterYearMobile.MultipleRangesSelected>
      ) : (
        <SearchFilterYearMobile.YearSliderKeys>
          <span>{update?.[0]}</span>
          <span>
            {update?.[1]}
            {shouldShowPlusSign(formattedSelectedFilterValues, isUpcomingReleasesSelected) && '+'}
          </span>
        </SearchFilterYearMobile.YearSliderKeys>
      )}
      <SearchFilterYearMobile.SliderWrapper>
        {areMultipleRangesSelected ? (
          <SearchFilterYearMobile.Line />
        ) : (
          <Slider
            data-qa="year_filter_slider"
            handleSlideEnd={handleSlideEnd}
            handleUpdate={handleSlideUpdate}
            range={range}
            step={1}
            values={values}
          />
        )}
      </SearchFilterYearMobile.SliderWrapper>
      <div>
        {options?.map((option) => {
          if (option.count < 1) {
            return;
          }
          return (
            <SearchFilterYearMobile.SearchBoxButton
              data-qa="mobile_filter_year_button"
              key={option.value}
              onClick={() => handleOnChange(option.value)}
              $isActive={formattedSelectedFilterValues?.includes(option.value)}
            >
              {getDateRangeStrings(option.display_name)}
            </SearchFilterYearMobile.SearchBoxButton>
          );
        })}
        <SearchFilterYearMobile.SearchBoxButton
          onClick={handleUpcomingReleasesClick}
          $isActive={isUpcomingReleasesSelected}
        >
          {t(`${TP}.upcomingReleases`, 'Upcoming Releases')}
        </SearchFilterYearMobile.SearchBoxButton>
      </div>
    </>
  );
};

SearchFilterYearMobile.SearchBoxButton = styled(SearchBoxButton)<{ $isActive: boolean }>`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  border: 1px solid ${({ theme }) => theme.navigation.borderColor};
  color: ${({ theme }) => theme.navigation.textColor};
  ${({ $isActive }) =>
    $isActive &&
    css`
      color: ${({ theme }) => theme.navigation.backgroundColor};
      background-color: ${({ theme }) => theme.navigation.loaderColor};
    `}
  &:hover {
    text-decoration: none;
  }
`;

SearchFilterYearMobile.Line = styled.hr`
  background-color: ${({ theme }) => theme.colors.black};
  opacity: 1;
`;

SearchFilterYearMobile.MultipleRangesSelected = styled.div`
  text-align: center;
`;

SearchFilterYearMobile.YearSliderKeys = styled.div`
  display: flex;
  justify-content: space-between;
`;

SearchFilterYearMobile.SliderWrapper = styled.div`
  margin: 24px 8px;
`;
